// react and js
import { React, useState, useRef } from "react";
import PropTypes from "prop-types";

// mui
import { Stack, Box, IconButton, DialogTitle, DialogContent, DialogActions, Typography, Divider, Chip } from "@mui/material";

// components
import ModalDialog from "../../../components/ModalDialog";
import Iconify from "../../../components/Iconify";

// theme
import { B1Tiny, ModalActionsLoadingButton } from "../../../theme/styled";

// render functions
import { TradeBasicsRenderParameters } from "./HandleTradeBasics";
import { InitialStopLossRenderParameters } from "./HandleInitialStopLoss";
import { StopLossTrailingsRenderParameters } from "./HandleStopLossTrailings";
import { InitialTakeProfitRenderParameters } from "./HandleInitialTakeProfit";
import { TakeProfitDynamicsRenderParameters } from "./HandleTakeProfitDynamics";
import { SymbolAndTimeframeRenderParameters } from "./HandleSymbolAndTimeframe";
import { VolumeCalculationRenderParameters } from "./HandleVolumeCalculation";
import { PendingOrderSettingsRenderParameters } from "./HandlePendingOrderSettings";
import { TradeSplittingsRenderParameters } from "./HandleTradeSplittings";
import { TimingRenderParameters } from "./HandleTiming";
import { MonitoringRenderParameters } from "./HandleMonitoring";
import { DataFeedbackRenderParameters } from "./HandleDataFeedback";
import { MiscellaneousRenderParameters } from "./HandleMiscellaneous";
import { NotificationsRenderParameters } from "./HandleNotifications";
import { IndicatorsRenderParameters } from "./HandleIndicators";
import { IndicatorCombinationsRenderParameters } from "./HandleIndicatorCombinations";
import { ChartAnalyzersRenderParameters } from "./HandleChartAnalyzers";
import { NewsRenderParameters } from "./HandleNews";
import { CandlePatternsRenderParameters } from "./HandleCandlePatterns";
import { ChartPatternsRenderParameters } from "./HandleChartPatterns";
import { FibonacciPatternsRenderParameters } from "./HandleFibonacciPatterns";

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function HandlePrintView({ moduleData, moduleName, professionalMode }) {
  // ----------------------------------------------------------------------
  // Properties
  // ----------------------------------------------------------------------
  HandlePrintView.propTypes = {
    moduleData: PropTypes.object.isRequired,
    moduleName: PropTypes.string.isRequired,
    professionalMode: PropTypes.bool.isRequired,
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const [open, setOpen] = useState(false);
  const contentRef = useRef(null);
  const [scroll, setScroll] = useState("paper");

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClose = () => setOpen(false);

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handlePrint = () => {
    const printContent = contentRef.current;
    const winPrint = window.open("", "", "left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0");

    winPrint.document.write(`
      <html>
        <head>
          <title>Trading Module: ${moduleName}</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
              color: #333;
            }
            h1 { color: black; border-bottom: 3px solid black; padding-bottom: 10px; margin-top: 0; }
            h2 { color: black; margin-top: 5px; padding-left: 15px; }
            h3 { color: black; margin-top: 5px; }
            .section { margin-bottom: 15px; }
            .parameter { margin: 8px 0; padding: 8px; background-color: #f8f9fa; }
            .parameter-name { font-weight: 600; color: black; }
            .parameter-value { margin-left: 10px; }
            .chip { 
              display: inline-block; 
              padding: 4px 8px; 
              margin: 2px; 
              background-color: #e3f2fd; 
              font-size: 18px; 
              color: black;
            }
            ul { padding-left: 20px; }
            li { margin: 5px 0; }
            .summary-box { 
              background-color: #f5f5f5; 
              padding: 15px; 
              margin: 15px 0; 
              border-left: 3px solid #73E555;
            }
            .grid { 
              display: grid; 
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
              gap: 15px; 
            }
            /* Parameter layout styling - target flex containers */
            .summary-box > div > div[style*="display: flex"] {
              display: flex !important;
              justify-content: space-between !important;
              align-items: flex-start !important;
              width: 100% !important;
              margin-bottom: 6px !important;
              gap: 20px !important;
            }
            .summary-box > div > div[style*="display: flex"] > p:first-child,
            .summary-box > div > div[style*="display: flex"] > span:first-child {
              font-weight: 600 !important;
              text-align: left !important;
              flex: 1 1 50% !important;
              margin: 0 !important;
            }
            .summary-box > div > div[style*="display: flex"] > p:last-child,
            .summary-box > div > div[style*="display: flex"] > span:last-child {
              text-align: right !important;
              flex: 1 1 50% !important;
              margin: 0 !important;
              word-break: break-word !important;
            }
            /* Alternative approach for Stack containers */
            .summary-box .MuiStack-root > div {
              display: flex !important;
              justify-content: space-between !important;
              width: 100% !important;
              margin-bottom: 6px !important;
              gap: 20px !important;
            }
            .summary-box .MuiStack-root > div > p {
              margin: 0 !important;
            }
            .summary-box .MuiStack-root > div > p:first-child {
              font-weight: 600 !important;
              text-align: left !important;
              flex: 1 1 50% !important;
            }
            .summary-box .MuiStack-root > div > p:last-child {
              text-align: right !important;
              flex: 1 1 50% !important;
              word-break: break-word !important;
            }
            @media print {
              body { margin: 0; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);

    winPrint.document.close();
    winPrint.focus();
    winPrint.print();
    winPrint.close();
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const handleClickOpen = (scrollType) => () => {
    setOpen(true);
    setScroll(scrollType);
  };

  // ----------------------------------------------------------------------
  // Section
  // ----------------------------------------------------------------------
  const renderModuleBasics = (moduleData) => (
<Box sx={{ mt: 1 }}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="contentSmall">
      <b>Module Number:</b>
    </Typography>
    <Typography variant="contentSmall">
      {moduleData.basics?.nNumber || "Not set"}
    </Typography>
  </Box>
  
  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
    <Typography variant="contentSmall">
      <b>Description:</b>
    </Typography>
    <Typography variant="contentSmall">
      {moduleData.basics?.strIdeaOutputDescription || "No description provided"}
    </Typography>
  </Box>
  
  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="contentSmall">
      <b>Version:</b>
    </Typography>
    <Typography variant="contentSmall">
      {moduleData.basics?.dVersion || "3.1"}
    </Typography>
  </Box>
</Box>
  );

  // ----------------------------------------------------------------------
  // Main component
  // ----------------------------------------------------------------------
  return (
    <>
      <B1Tiny fullWidth disableRipple={Boolean(true)} onClick={handleClickOpen("paper")}>
        Print view
      </B1Tiny>

      <ModalDialog
        open={open}
        handleClose={handleClose}
        scroll={scroll}
        maxWidth="md"
        outlet={
          <>
            <DialogTitle
              id="dialog-title"
              sx={{
                background: "#F2F2F2",
                textTransform: "none",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                alignContent: "center",
                py: 1,
                pl: 2,
                pr: 1,
              }}
            >
              <Typography variant="contentBig">Print view</Typography>

              <IconButton onClick={handleClose} sx={{ borderRadius: "10px" }}>
                <Iconify icon="eva:close-outline" minWidth={25} minHeight={25} color="#2E2E2E" />
              </IconButton>
            </DialogTitle>

            <DialogContent
              dividers
              sx={{
                background: "#F2F2F2",
                borderTop: "1px solid #C8C8C8",
                borderBottom: "0px solid #C8C8C8",
                px: 2,
                pt: 2,
                pb: 1,
                width: "100%",
                boxSizing: "border-box",
                overflowY: "scroll",
                overflowX: "hidden",
                // Custom scrollbar styling
                "&::-webkit-scrollbar": {
                  width: "16px",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "#e0e0e0",
                  borderRadius: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "#888888",
                  borderRadius: "6px",
                  border: "2px solid #e0e0e0",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "#666666",
                },
                // Firefox scrollbar styling
                scrollbarWidth: "thin",
                scrollbarColor: "#888888 #e0e0e0",
              }}
            >
              <Box ref={contentRef}>
                <Stack spacing={2}>
                  {/* ---------------------------------------------------------------------- */}
                  {/* Headline */}
                  {/* ---------------------------------------------------------------------- */}
                  <Typography variant="h3" component="h1" sx={{ fontWeight: 600 }}>
                    {moduleName}
                  </Typography>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mb: 1, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Basics */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600 }}>
                      Basics
                    </Typography>
                    {renderModuleBasics(moduleData)}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Trade Basics */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Trade Basics
                    </Typography>
                    {TradeBasicsRenderParameters(professionalMode, moduleData.tradeBasics || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Pending Order Settings */}
                  {/* ---------------------------------------------------------------------- */}
                  {professionalMode && (
                    <Box
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        Pending Order Settings
                      </Typography>
                      {PendingOrderSettingsRenderParameters(
                        professionalMode,
                        moduleData.pendingOrderSettings || {},
                        moduleData.indicators || [],
                        moduleData.chartAnalyzers || []
                      )}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Indicators */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.indicators || []).map((indicator, index) => (
                    <Box
                      key={`indicator-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Indicator
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {indicator.name || `Indicator ${index + 1}`}
                      </Typography>
                      {indicator.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {indicator.subtitle}
                        </Typography>
                      )}
                      {IndicatorsRenderParameters(indicator.id || index, () => indicator.config || {}, professionalMode)}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Indicator Combinations */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.indicatorCombinations || []).map((combination, index) => (
                    <Box
                      key={`indicator-combination-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Indicator Combination
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {combination.name || `Indicator Combination ${index + 1}`}
                      </Typography>
                      {combination.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {combination.subtitle}
                        </Typography>
                      )}
                      {IndicatorCombinationsRenderParameters(
                        combination.id || index,
                        () => combination.config || {},
                        professionalMode,
                        moduleData.indicators || []
                      )}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Chart Analyzers */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.chartAnalyzers || []).map((analyzer, index) => (
                    <Box
                      key={`chart-analyzer-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Chart Analyzer
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {analyzer.name || `Chart Analyzer ${index + 1}`}
                      </Typography>
                      {analyzer.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {analyzer.subtitle}
                        </Typography>
                      )}
                      {ChartAnalyzersRenderParameters(analyzer.id || index, () => analyzer.config || {}, professionalMode)}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* News */}
                  {/* ---------------------------------------------------------------------- */}
                  {moduleData.news && Object.keys(moduleData.news).length > 0 && (
                    <Box
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        News
                      </Typography>
                      {NewsRenderParameters(professionalMode, moduleData.news)}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Candle Patterns */}
                  {/* ---------------------------------------------------------------------- */}
                  {moduleData.candlePatterns && Object.keys(moduleData.candlePatterns).length > 0 && (
                    <Box
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        Candle Patterns
                      </Typography>
                      {CandlePatternsRenderParameters(professionalMode, moduleData.candlePatterns)}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Chart Patterns */}
                  {/* ---------------------------------------------------------------------- */}
                  {moduleData.chartPatterns && Object.keys(moduleData.chartPatterns).length > 0 && (
                    <Box
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        Chart Patterns
                      </Typography>
                      {ChartPatternsRenderParameters(professionalMode, moduleData.chartPatterns)}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Fibonacci Patterns */}
                  {/* ---------------------------------------------------------------------- */}
                  {moduleData.fibonacciPatterns && Object.keys(moduleData.fibonacciPatterns).length > 0 && (
                    <Box
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        Fibonacci Patterns
                      </Typography>
                      {FibonacciPatternsRenderParameters(professionalMode, moduleData.fibonacciPatterns)}
                    </Box>
                  )}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Initial Stop Loss */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Initial Stop Loss
                    </Typography>
                    {InitialStopLossRenderParameters(
                      professionalMode,
                      moduleData.initialStopLoss || {},
                      moduleData.indicators || [],
                      moduleData.chartAnalyzers || []
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Stop Loss Trailing */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.stopLossTrailings || []).map((trailing, index) => (
                    <Box
                      key={`stop-loss-trailing-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Stop Loss Trailing
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {trailing.name || `Stop loss trailing ${index + 1}`}
                      </Typography>
                      {trailing.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {trailing.subtitle}
                        </Typography>
                      )}
                      {StopLossTrailingsRenderParameters(trailing.id || index, () => trailing.config || {}, professionalMode)}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Initial Take Profit */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Initial Take Profit
                    </Typography>
                    {InitialTakeProfitRenderParameters(
                      professionalMode,
                      moduleData.initialTakeProfit || {},
                      moduleData.indicators || [],
                      moduleData.chartAnalyzers || []
                    )}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Dynamic Take Profit */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.takeProfitDynamics || []).map((dynamic, index) => (
                    <Box
                      key={`take-profit-dynamic-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Dynamic Take Profit
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {dynamic.name || `Dynamic take profit ${index + 1}`}
                      </Typography>
                      {dynamic.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {dynamic.subtitle}
                        </Typography>
                      )}
                      {TakeProfitDynamicsRenderParameters(dynamic.id || index, () => dynamic.config || {}, professionalMode)}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Symbol and Timeframe */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Symbol and Timeframe
                    </Typography>
                    {SymbolAndTimeframeRenderParameters(professionalMode, moduleData.symbolAndTimeframe || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Volume Calculation */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Volume Calculation
                    </Typography>
                    {VolumeCalculationRenderParameters(professionalMode, moduleData.volumeCalculation || {}, moduleData.indicators || [])}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Trade Splitting */}
                  {/* ---------------------------------------------------------------------- */}
                  {(moduleData.tradeSplittings || []).map((splitting, index) => (
                    <Box
                      key={`trade-splitting-${index}`}
                      className="summary-box"
                      sx={{
                        backgroundColor: "white",
                        p: 2,
                        borderRadius: "10px",
                        mb: 3,
                        border: "1px solid #C8C8C8",
                      }}
                    >
                      <Typography variant="h5" sx={{ fontWeight: 500, mb: 1, color: "primary.main" }}>
                        Trade Splitting
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                        {splitting.name || `Trade splitting ${index + 1}`}
                      </Typography>
                      {splitting.subtitle && (
                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
                          {splitting.subtitle}
                        </Typography>
                      )}
                      {TradeSplittingsRenderParameters(splitting.id || index, () => splitting.config || {}, professionalMode)}
                    </Box>
                  ))}

                  {/* ---------------------------------------------------------------------- */}
                  {/* Timing */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Timing
                    </Typography>
                    {TimingRenderParameters(professionalMode, moduleData.timing || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Monitoring */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Monitoring
                    </Typography>
                    {MonitoringRenderParameters(professionalMode, moduleData.monitoring || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Data Feedback */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Data Feedback
                    </Typography>
                    {DataFeedbackRenderParameters(professionalMode, moduleData.dataFeedback || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Notifications */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Notifications
                    </Typography>
                    {NotificationsRenderParameters(professionalMode, moduleData.notifications || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Miscellaneous */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box
                    className="summary-box"
                    sx={{
                      backgroundColor: "white",
                      p: 2,
                      borderRadius: "10px",
                      mb: 3,
                      border: "1px solid #C8C8C8",
                    }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                      Miscellaneous
                    </Typography>
                    {MiscellaneousRenderParameters(professionalMode, moduleData.Miscellaneous || {})}
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Divider */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box>
                    <Divider sx={{ mt: 2, mb: 0, borderColor: "#C8C8C8" }} />
                  </Box>

                  {/* ---------------------------------------------------------------------- */}
                  {/* Footer */}
                  {/* ---------------------------------------------------------------------- */}
                  <Box sx={{ pt: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <Typography variant="contentSmall" sx={{ lineHeight: 2 }}>
                      Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                    </Typography>
                    <Typography variant="contentSmall">Expert Advisor Builder v{moduleData.basics?.dVersion || "3.1"}</Typography>
                  </Box>
                </Stack>
              </Box>
            </DialogContent>

            <DialogActions
              sx={{
                background: "#F2F2F2",
                p: 2,
                pt: 1,
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
                width: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ pt: 1, width: "100%", display: "flex", gap: 1 }}>
                <ModalActionsLoadingButton fullWidth size="large" onClick={handlePrint}>
                  Print module
                </ModalActionsLoadingButton>

                <ModalActionsLoadingButton fullWidth size="large" variant="contained" onClick={handleClose}>
                  Close
                </ModalActionsLoadingButton>
              </Box>
            </DialogActions>
          </>
        }
      />
    </>
  );
}
